// The condition builder's logic, with no React and no vocabulary in it.
//
// THIS MODULE HOLDS NO FIELD NAME, NO OPERATOR NAME AND NO ENUM VALUE. Not one of
// `assignee_id`, `custom_status_id`, `is_not`, `not_includes`, `on_hold` or
// `urgent` appears anywhere in it or in ConditionBuilder.jsx — every list arrives
// in `vocabulary`, served on the `saved-views` descriptor and projected from
// src/views/registry.js's own `FIELDS` map, so the operators this screen offers
// are the ARRAY OBJECT `assertOperator` tests against. Even the six live-list
// endpoints are served rather than known here: a source's name is its path, and
// which column it stores (`id` for a queue, `name` for a tag, `key` for a custom
// field) is a fact about how the registry COMPILES that field, so it is declared
// beside the compile rule and read off the spec here.
//
// That property cannot be restated wholesale for `web/src` and is not claimed to
// be: BASE_STATUSES is exported from web/src/components/tickets/format.js and
// TicketFilters, Reports and New each hold their own copies for the TICKET
// screens. The claim is scoped to this builder, and `grep` holds it.
//
// NOTHING IS EVER DROPPED, and there are TWO reasons a row can be uneditable —
// which matters more here than it did for macros, because the polarity is
// inverted. An unknown macro verb is inert: `src/routing/actions.js` and New.jsx
// both skip it. An unknown view FIELD is not inert at all — `getField` throws,
// `compileViewFor` catches, and the WHOLE VIEW becomes MATCH_NONE. One bad row
// blanks the entire queue. So a locked row says which of the two it is:
//
//   'locked'   a real registry field the console has no safe control for
//              (brand_id, organization_id, requester_id — see the note on
//              `authorable` in src/services/admin/views.js). The filter WORKS.
//   'unknown'  a field or operator the registry does not have. The view matches
//              NOTHING until it is fixed, and the row has to say so.
//
// Both are kept verbatim and re-emitted byte-identical; removal is always explicit.

import { humanize } from './macro-actions.js';

export { humanize };

/**
 * The served vocabulary, or null when the server did not describe one.
 *
 * Null is the safe answer and the caller's contract is to fall back to the JSON
 * textarea — an older API behind a separately deployed `web/dist` is a real
 * possibility. Anything malformed is treated as absent for the same reason:
 * there is no client list to guess from, which is exactly what makes a stale
 * client unable to render a WRONG dropdown.
 */
export function conditionVocabulary(field) {
  const vocabulary = field?.vocabulary;
  if (!vocabulary || typeof vocabulary !== 'object') return null;
  const { fields, arity } = vocabulary;
  if (!Array.isArray(fields) || fields.length === 0) return null;
  if (!fields.every((f) => f && typeof f.field === 'string' && f.field.length > 0
    && Array.isArray(f.operators) && f.operators.length > 0
    && f.value && typeof f.value.kind === 'string')) return null;
  if (!arity || typeof arity !== 'object') return null;
  return vocabulary;
}

const isPlainObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const isScalar = (v) => typeof v === 'string' || typeof v === 'number';
const own = (o, k) => (Object.hasOwn(o, k) ? o[k] : undefined);

/** The two groups, in the order the compiler reads them. Not a vocabulary — the
 *  shape `compileConditions` insists on, and the only two keys it will read. */
const GROUP_KEYS = ['all', 'any'];

/** The server's own cap on `all.length + any.length`, mirrored so the `+` stops
 *  where the server does rather than letting somebody build row 41 and meet it as
 *  a 422. Falls back to the compiler's documented figure if it was not served. */
export function maxConditionRows(vocabulary) {
  const served = vocabulary?.max_conditions;
  return Number.isInteger(served) && served > 0 ? served : 40;
}

export function specFor(field, vocabulary) {
  return (vocabulary?.fields ?? []).find((entry) => entry.field === field) ?? null;
}

/** The fields the Add control may offer — everything the server marked authorable. */
export function authorableFields(vocabulary) {
  return (vocabulary?.fields ?? []).filter((entry) => entry.value?.authorable === true);
}

/**
 * A field key as a human reads it, derived MECHANICALLY: underscores to spaces,
 * initial capital, and a trailing " id" dropped. A label map would be a second
 * copy of the vocabulary and a field added tomorrow would render blank, while
 * `custom_status_id` -> "Custom status" and `created_at` -> "Created at" need no
 * maintenance and cannot drift.
 */
export function fieldLabel(field) {
  return humanize(field).replace(/ id$/, '');
}

/**
 * A sort key as a human reads it. The leading `-` is DESCENDING — a syntax rule of
 * the value, not a word in a vocabulary, so reading it here adds no list to drift.
 * Every key in `SORT_VALUES` names a timestamp column, so "newest"/"oldest" is
 * literally what the direction means for all of them.
 */
export function sortLabel(value) {
  const text = String(value ?? '');
  const descending = text.startsWith('-');
  const base = humanize(descending ? text.slice(1) : text);
  if (base === '') return text;
  return `${base}, ${descending ? 'newest first' : 'oldest first'}`;
}

/**
 * Which control a field+operator pair's value takes. The component asks this and
 * knows nothing itself, so a field the server adds tomorrow renders with a real
 * control instead of vanishing.
 *
 * `arity` comes off the served map rather than being inferred from the operator's
 * spelling — that is the whole reason it is served.
 */
export function valueControlFor(field, operator, vocabulary) {
  const spec = specFor(field, vocabulary);
  const value = spec?.value;
  // `Object.hasOwn`, not a bare read: `arity` arrives as parsed JSON, and
  // `arity['constructor']` on a plain object is a FUNCTION off Object.prototype
  // rather than the 'single'/'set'/'none' this switches on.
  const served = vocabulary?.arity;
  const arity = served && Object.hasOwn(served, operator) ? served[operator] : 'single';
  if (!value) return { kind: 'text', arity: 'single' };
  if (arity === 'none' && value.kind !== 'keyed_value') return { kind: 'none', arity };

  const base = {
    arity,
    source: value.source ?? null,
    valueKey: value.valueKey ?? null,
    labelKey: value.labelKey ?? null,
    hintKey: value.hintKey ?? null,
    includeInactive: value.includeInactive === true,
    nobody: value.nobody === true,
    sentinels: value.sentinels ?? [],
    ordered: value.ordered === true,
    max: value.max ?? null,
  };

  switch (value.kind) {
    case 'enum':
      return {
        ...base,
        kind: 'enum',
        closed: true,
        // Order is meaning when `ordered` is set: `less_than` expands into the
        // PREFIX of this list, so the list as served IS the workflow.
        options: value.values.map((v) => ({ value: String(v), label: humanize(v) })),
      };
    case 'id':
      return { ...base, kind: 'id', closed: true, options: [] };
    case 'name_list':
      // A combobox, not a locked list: ticket writes create unknown tag names BY
      // NAME, so a view filtering on a tag nobody has applied yet is authorable —
      // while a plain text box makes a typo permanent and silent, because tags
      // refuse deletion and conditions match by name.
      return { ...base, kind: 'name_list', closed: false, options: [] };
    case 'keyed_value':
      return { ...base, kind: 'keyed_value', closed: false, options: [] };
    case 'hours':
      return { ...base, kind: 'hours', closed: false, options: [] };
    default:
      return { ...base, kind: 'text', closed: false, options: [] };
  }
}

/**
 * One live row from a list endpoint, turned into an option using the keys the
 * SERVER named — so the browser has no table of its own mapping a source to the
 * column it stores.
 *
 * EVERY interpolation here is type-guarded, and that is not defensive habit: the
 * `[object Object]` class of bug shipped on these admin screens once already, and
 * it is live in the registry's own tags path today (`String({})` bound as the two
 * tag names ["[object","Object]"], with no error and no symptom). A row is JSON
 * from an endpoint this screen does not own; a label that is null, missing, or an
 * object must degrade to the stored value, never to the words "[object Object]".
 *
 * `valueKey` is the column the REGISTRY binds and is not always `id` — tags compile
 * on `g.name` and custom fields on `d.[key]` — so a row whose value column is
 * missing yields NO option at all rather than an option that stores "undefined".
 */
export function sourceOptions(rows, control) {
  const text = (v) => (typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''));
  return (rows ?? []).map((row) => {
    if (row === null || typeof row !== 'object') return null;
    const value = text(row[control?.valueKey]);
    if (value === '') return null;
    const label = text(row[control?.labelKey]) || value;
    const hint = control?.hintKey ? text(row[control.hintKey]) : '';
    return { value, label: hint === '' ? label : `${label} · ${hint}` };
  }).filter(Boolean);
}

/** Whether a stored value is something the control above can actually show. */
function representable(control, value) {
  switch (control.kind) {
    case 'none':
      return true;
    case 'enum':
      return control.arity === 'set'
        ? (isScalar(value) || (Array.isArray(value) && value.every(isScalar)))
        : isScalar(value);
    case 'id':
      if (value === '' || value === null || value === undefined) return true;
      return control.arity === 'set'
        ? (isScalar(value) || (Array.isArray(value) && value.every(isScalar)))
        : isScalar(value);
    case 'name_list':
      return isScalar(value) || (Array.isArray(value) && value.every(isScalar));
    case 'keyed_value':
      return isPlainObject(value) && typeof value.key === 'string';
    case 'hours':
      return isScalar(value) || value === undefined || value === null;
    default:
      return false;
  }
}

/**
 * A stored set value as a list, without rewriting what is stored.
 *
 * The registry accepts a SCALAR everywhere it accepts an array — `custom_status_id
 * not_includes "3"` is exactly `is_not "3"`, and a tags value is legitimately one
 * SPACE-SEPARATED string ("backup_overage new_store" is what real views carry).
 * This is the read side only: `row.value` keeps the stored shape until somebody
 * edits it, which is what stops a checkbox being ticked from rewriting a string
 * into an array nobody asked for.
 */
export function asList(value, control) {
  if (Array.isArray(value)) return value.map(String);
  if (value === '' || value === null || value === undefined) return [];
  if (control?.kind === 'name_list') return String(value).split(/\s+/).filter(Boolean);
  return [String(value)];
}

/**
 * Stored JSON to a builder draft.
 *
 *   { groups: ['all'], rows: [ … ] }
 *
 * `groups` carries WHICH groups the stored object had, in its own key order, so a
 * view stored as `{"all":[…]}` is not re-emitted as `{"all":[…],"any":[]}` — that
 * difference is a phantom diff on every save, and `{"all":[],"any":[]}` happens to
 * be the column DEFAULT this whole screen exists to keep out of an active view.
 *
 * Every entry produces exactly one row; the lengths are preserved without
 * exception.
 */
export function conditionsToDraft(conditions, vocabulary) {
  const groups = [];
  const rows = [];
  if (!isPlainObject(conditions)) return { groups: ['all'], rows };

  for (const group of GROUP_KEYS) {
    if (!Object.hasOwn(conditions, group)) continue;
    const list = conditions[group];
    if (!Array.isArray(list)) continue;
    groups.push(group);
    list.forEach((entry, i) => rows.push(toRow(entry, group, `${group}-${i}`, vocabulary)));
  }
  // A draft with no group at all cannot be edited into anything the compiler will
  // read, so the builder starts one — which is also what a brand new view needs.
  return { groups: groups.length > 0 ? groups : ['all'], rows };
}

function toRow(entry, group, id, vocabulary) {
  const locked = (kind, raw) => ({ id, group, kind, field: null, operator: null, value: null, raw });
  if (!isPlainObject(entry)) return locked('unknown', entry);

  const field = own(entry, 'field');
  const operator = own(entry, 'operator');
  const value = own(entry, 'value');
  const spec = typeof field === 'string' ? specFor(field, vocabulary) : null;

  // The registry has never heard of this field: `getField` throws and the whole
  // view compiles to MATCH_NONE.
  if (!spec) return locked('unknown', entry);
  // A real field with a control this console cannot safely offer. The filter works.
  if (spec.value?.authorable !== true) return locked('locked', entry);
  // The registry knows the field but not this operator: `assertOperator` throws,
  // and again the whole view is MATCH_NONE.
  if (typeof operator !== 'string' || !spec.operators.includes(operator)) {
    return locked('unknown', entry);
  }

  const control = valueControlFor(field, operator, vocabulary);
  // A value shape no control can show — the `[object Object]` class of bug, which
  // is live in the registry's own tags path today. Locked rather than coerced:
  // `String({})` is exactly the "[object Object]" that shipped on the macro screen.
  if (!representable(control, value)) return locked('locked', entry);

  return { id, group, kind: 'known', field, operator, value, raw: entry };
}

/** Whether a known row still says exactly what it was loaded saying. */
function pristine(row) {
  if (!isPlainObject(row.raw)) return false;
  return own(row.raw, 'field') === row.field
    && own(row.raw, 'operator') === row.operator
    && JSON.stringify(own(row.raw, 'value')) === JSON.stringify(row.value);
}

/**
 * A builder draft back to stored JSON.
 *
 * THE PHANTOM-DIFF RULE. `changedValues` compares objects with `JSON.stringify`,
 * so a builder that re-emits `{operator, field, value}`, or `["x"]` where the row
 * held `"x"`, makes every untouched view look edited and fills every
 * `config_events` before/after diff with changes nobody made. An UNTOUCHED row is
 * therefore re-emitted as its original object, byte for byte, key order and extra
 * keys and all. Only a row somebody actually changed is rewritten, and then in the
 * shape the registry and the migration importer both write: `{field, operator,
 * value}`, in that key order.
 */
export function draftToConditions(draft) {
  const out = {};
  for (const group of (draft?.groups ?? ['all'])) {
    out[group] = (draft?.rows ?? [])
      .filter((row) => row.group === group)
      .map((row) => (row.kind === 'known' && !pristine(row)
        ? { field: row.field, operator: row.operator, value: row.value }
        : row.raw));
  }
  return out;
}

export function conditionCount(draft) {
  return (draft?.rows ?? []).length;
}

export function canAddConditionRow(draft, vocabulary) {
  return conditionCount(draft) < maxConditionRows(vocabulary);
}

/**
 * A fresh row: a served field, its first operator, and a BLANK value.
 *
 * Blank must be a row PROBLEM that blocks Save — better than posting a value the
 * server would refuse, and far better than a row that is already a complete
 * filter nobody chose. That last case is why the field is not simply the first
 * one served: on an id field `""` is not "unset", it is `IS NULL`, so a new row
 * on `assignee_id` would arrive meaning "assigned to nobody" and save clean.
 * Measured, not imagined — it is what this function did first.
 *
 * So: the first authorable field whose blank value is not already an answer,
 * which is a closed list needing an explicit pick. Mechanical, from the served
 * spec, with the plain first field as the floor if that ever finds nothing.
 */
export function newConditionRow(vocabulary, group, seq) {
  const offered = authorableFields(vocabulary);
  const first = offered.find((entry) => entry.value?.nobody !== true) ?? offered[0] ?? null;
  return {
    id: `new-${seq}`,
    group,
    kind: 'known',
    field: first?.field ?? '',
    operator: first?.operators?.[0] ?? '',
    value: '',
    raw: null,
  };
}

/**
 * Changing the FIELD clears the operator to that field's first and the value to
 * blank. Carrying a status across to `priority` is a guaranteed refusal, and a
 * value silently kept from another vocabulary is the exact quiet rewrite this
 * design exists to prevent.
 */
export function refieldRow(row, field, vocabulary) {
  const spec = specFor(field, vocabulary);
  return { ...row, field, operator: spec?.operators?.[0] ?? '', value: '' };
}

/**
 * Changing the OPERATOR keeps the value only when the control does not change
 * shape — switching `is` to `includes` turns one answer into a list, and switching
 * to `present` drops the value entirely. Carrying a scalar into a set control is
 * how a stored `""` becomes a set containing nothing, which is a silent MATCH_NONE.
 */
export function reoperateRow(row, operator, vocabulary) {
  const was = valueControlFor(row.field, row.operator, vocabulary);
  const now = valueControlFor(row.field, operator, vocabulary);
  const keep = was.kind === now.kind && was.arity === now.arity;
  return { ...row, operator, value: keep ? row.value : '' };
}

export function setRowValue(row, value) {
  return { ...row, value };
}

/** Which groups a draft may show. `any` appears only once somebody asks for it. */
export function withGroup(draft, group) {
  if (draft.groups.includes(group)) return draft;
  return { ...draft, groups: [...GROUP_KEYS].filter((g) => draft.groups.includes(g) || g === group) };
}

export function removeRow(draft, id) {
  return { ...draft, rows: draft.rows.filter((row) => row.id !== id) };
}

export function replaceRow(draft, id, next) {
  return { ...draft, rows: draft.rows.map((row) => (row.id === id ? next : row)) };
}

export function addRow(draft, row) {
  return { ...withGroup(draft, row.group), rows: [...draft.rows, row] };
}

/**
 * Keep a stored value visible even when its list no longer offers it.
 *
 * A user's `can_be_assigned` flips from an Entra role change, a status or a form
 * can be deactivated, a tag can be renamed on the way in. The console does not get
 * to rewrite a view because a lookup missed: the option stays, carrying the stored
 * value, labelled so a lead can see why it looks odd. Hiding it is how they never
 * find out.
 */
export function withStoredOptions(options, values) {
  const list = options ?? [];
  const known = new Set(list.map((option) => option.value));
  const extra = [];
  for (const value of values ?? []) {
    if (value === '' || value === null || value === undefined) continue;
    const text = String(value);
    if (known.has(text) || extra.some((o) => o.value === text)) continue;
    extra.push({ value: text, label: `${text} (not in the list)` });
  }
  return extra.length === 0 ? list : [...list, ...extra];
}

/**
 * Per-row problems, checked here so the builder never posts something the server
 * has already said it will refuse — and so the message names the row rather than
 * arriving as one 422 for a form with twenty controls on it.
 *
 * This is deliberately a SUBSET of src/services/admin/views.js. The compile probe,
 * the bigint range bound and the two `custom_field_defs` / `ticket_statuses`
 * existence checks are the server's and stay the server's: they need the registry
 * and the database. What is here is everything a lead can see on screen and fix
 * without a round trip. A locked or unknown row is NEVER a problem — it blocks
 * nothing, exactly as an unknown macro verb does not.
 */
export function conditionProblems(draft, vocabulary) {
  const problems = [];
  const rows = draft?.rows ?? [];

  if (rows.length === 0) {
    // The whole point of the screen. `{"all":[]}` compiles to `(1=1)` and is the
    // column DEFAULT, and `is_active` DEFAULTs to 1 — so a view saved with nothing
    // in it is a named queue showing the entire desk.
    problems.push({
      id: null,
      message: 'A view with no conditions matches every ticket. Add at least one condition.',
    });
  }
  if (rows.length > maxConditionRows(vocabulary)) {
    problems.push({
      id: null,
      message: `A view can hold at most ${maxConditionRows(vocabulary)} conditions.`,
    });
  }

  rows.forEach((row, index) => {
    if (row.kind !== 'known') return;
    const control = valueControlFor(row.field, row.operator, vocabulary);
    const where = `Row ${index + 1} (${fieldLabel(row.field)})`;
    const fail = (message) => problems.push({ id: row.id, message: `${where}: ${message}` });

    if (control.kind === 'none') return;

    if (control.arity === 'set') {
      const list = asList(row.value, control);
      if (list.length === 0) { fail('choose at least one value.'); return; }
      if (list.some((v) => String(v).trim() === '')) { fail('remove the blank entry.'); return; }
      if (control.closed && control.kind === 'enum') {
        const bad = list.find((v) => !control.options.some((o) => o.value === v));
        if (bad) fail(`${JSON.stringify(bad)} is not a value this filter accepts.`);
      }
      return;
    }

    if (control.kind === 'keyed_value') {
      const value = isPlainObject(row.value) ? row.value : {};
      if (typeof value.key !== 'string' || value.key.trim() === '') { fail('choose a field.'); return; }
      // `present`/`not_present` carry the key and nothing else.
      if (control.arity === 'none') return;
      if (!isScalar(value.value) || String(value.value) === '') fail('enter a value to compare.');
      return;
    }

    if (control.kind === 'hours') {
      // `Number('')` is 0 and `Number.isInteger(0)` is true, so a blank age box
      // compiles to real SQL matching every ticket — with no `(1=1)` in it for
      // anything downstream to notice. This is the client half of the only
      // fail-open a scan of the compiled SQL cannot see.
      const text = isScalar(row.value) ? String(row.value).trim() : '';
      if (text === '') { fail('enter a number of hours.'); return; }
      const hours = Number(text);
      if (!Number.isInteger(hours)) { fail('hours must be a whole number.'); return; }
      if (hours < 0 || (control.max !== null && hours > control.max)) {
        fail(`hours must be between 0 and ${control.max}.`);
      }
      return;
    }

    // Single-valued: enum, id, tags.
    const text = isScalar(row.value) ? String(row.value) : '';
    if (control.kind === 'id' && (row.value === '' || row.value === null)) {
      // "" is idField's "nobody", which compiles to IS NULL. A real answer, not a
      // blank — this is what "Unassigned" is.
      return;
    }
    if (text.trim() === '') { fail('choose a value.'); return; }
    if (control.closed && control.kind === 'enum'
        && !control.options.some((o) => o.value === text)) {
      fail(`${JSON.stringify(text)} is not a value this filter accepts.`);
    }
  });

  return problems;
}

/**
 * Whether the row builder can be offered at all.
 *
 * False sends the screen to the JSON textarea, which is the floor and stays
 * permanently reachable. A stored non-object is what the column's `ISJSON(col,
 * OBJECT)` CHECK still allows through as `[]`, and a view holding something the
 * builder cannot express must not become both uneditable AND unviewable.
 */
export function builderAvailable(value, vocabulary) {
  if (vocabulary === null || vocabulary === undefined) return false;
  if (!isPlainObject(value)) return false;
  // A group that is present but not an array is the shape `compileConditions`
  // refuses outright ("coercing a non-array to [] is how a filter widens in
  // silence"). The builder would silently drop it; the JSON view shows it.
  return GROUP_KEYS.every((g) => !Object.hasOwn(value, g) || Array.isArray(value[g]));
}

/**
 * The `conditions` editor's text, read as a value. The failure that matters is the
 * SILENT one: text the editor cannot read must block the save, never resolve to
 * `{}` — and `{}` is itself refused here, because a conditions object with neither
 * group is one `compileConditions` throws on, which renders the view empty.
 */
export function parseConditionsDraft(text) {
  const trimmed = String(text ?? '').trim();
  if (trimmed === '') return { ok: false, error: 'Conditions cannot be empty.' };

  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    return { ok: false, error: `Not valid JSON: ${err.message}` };
  }
  if (!isPlainObject(parsed)) {
    return { ok: false, error: 'Conditions must be a JSON object with an "all" and/or "any" list.' };
  }
  if (!GROUP_KEYS.some((g) => Object.hasOwn(parsed, g))) {
    return { ok: false, error: 'Conditions must declare an "all" or "any" list.' };
  }
  return { ok: true, value: parsed, error: null };
}
